import type { AccountingPeriodView, UnlockAccountingPeriodInput } from '@chukua/common';
import { useState } from 'react';
import { useToast } from '../../../shared/hooks/use-toast';
import { ApiError } from '../../../shared/shared.api';
import { closeAccountingPeriod, lockAccountingPeriod, unlockAccountingPeriod } from '../accounting-periods.api';
import { useAccountingPeriodList } from './use-accounting-period-list';

const rangeOf = (period: AccountingPeriodView) => `${period.startDate} to ${period.endDate}`;

function messageFor(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.status === 409 ? 'This period has changed since the page loaded. Reload and try again.' : fallback;
}

/**
 * The periods list plus the moves. Close, lock and add are row or header actions with no form, so a failure toasts; unlock
 * has a form (its reason), so it reports its failure to the modal, which shows it inline.
 */
export function useAccountingPeriods() {
  const { state, list, isRefreshing, hasNoPeriods, reload } = useAccountingPeriodList();
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const toast = useToast();

  async function act(id: string, move: () => Promise<AccountingPeriodView>, done: (period: AccountingPeriodView) => string, failure: string) {
    setBusyId(id);
    try {
      const period = await move();
      await reload();
      toast.success(done(period));
    } catch (err) {
      toast.error(messageFor(err, failure));
    } finally {
      setBusyId(null);
    }
  }

  const close = (period: AccountingPeriodView) =>
    act(
      period.id,
      () => closeAccountingPeriod(period.id),
      (p) => `Closing started for ${rangeOf(p)}`,
      `Couldn't start closing ${rangeOf(period)}`,
    );
  const lock = (period: AccountingPeriodView) =>
    act(
      period.id,
      () => lockAccountingPeriod(period.id),
      (p) => `Period ${rangeOf(p)} locked`,
      `Couldn't lock ${rangeOf(period)}`,
    );

  /** Returns whether it succeeded, so the modal knows to close itself. */
  async function unlock(period: AccountingPeriodView, input: UnlockAccountingPeriodInput): Promise<boolean> {
    setUnlockError(null);
    setBusyId(period.id);
    try {
      await unlockAccountingPeriod(period.id, input);
      await reload();
      toast.success(`Period ${rangeOf(period)} unlocked`);
      return true;
    } catch (err) {
      setUnlockError(messageFor(err, "Couldn't unlock the period. Check the reason and try again."));
      return false;
    } finally {
      setBusyId(null);
    }
  }

  return {
    state,
    list,
    isRefreshing,
    busyId,
    close,
    lock,
    unlock,
    reload,
    hasNoPeriods,
    unlockError,
    resetUnlockError: () => setUnlockError(null),
  };
}
